import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const LiabilityWaiversPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />

      <main className="flex-grow">
        <div className="bg-black text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold mb-4">Liability Waivers</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-8 inline-flex items-center text-red-600 hover:text-red-700 font-medium"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>

          <div>
            <div className="prose max-w-none">
              
              {/* Important Notice */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>IMPORTANT:</strong> Please read this document carefully before participating in the tournament. 
                      By registering and participating, you acknowledge that you have read, understood, and agree to be bound by these terms.
                    </p>
                  </div>
                </div>
              </div>

              {/* General Release */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">General Release of Liability</h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    I acknowledge that participation in the Crash Champions Classic Golf Tournament ("Tournament") involves inherent risks, 
                    including but not limited to physical injury, property damage, or other losses that may result from my participation.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    In consideration for being permitted to participate in the Tournament, I hereby release, waive, discharge, and covenant not to sue 
                    Crash Champions, LLC, its officers, directors, employees, agents, sponsors, volunteers, and the golf course facility 
                    (collectively "Released Parties") from any and all liability, claims, demands, actions, and causes of action 
                    whatsoever arising out of or related to any loss, damage, or injury that may be sustained while participating in the Tournament.
                  </p>
                </div>
              </section>

              {/* Assumption of Risk */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Assumption of Risk</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  I acknowledge and understand that participating in the Tournament involves certain risks and hazards, including but not limited to:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Risk of injury from golf clubs, golf balls, and golf carts</li>
                  <li>• Slips, trips, and falls on the golf course</li>
                  <li>• Weather-related hazards including lightning, rain, and extreme temperatures</li>
                  <li>• Insect bites and allergic reactions</li>
                  <li>• Course conditions including uneven terrain, water hazards, and natural obstacles</li>
                  <li>• Equipment malfunction or failure</li>
                  <li>• Actions or inactions of other participants</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>I voluntarily assume all risks</strong> associated with participation in the Tournament, whether known or unknown, 
                  and whether arising before, during, or after the event.
                </p>
              </section>

              {/* Medical Emergency Authorization */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Medical Emergency Authorization</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <p className="text-red-800 leading-relaxed mb-4">
                    I hereby authorize the Released Parties to obtain emergency medical treatment for me in the event of injury or illness during the Tournament.
                  </p>
                  <ul className="space-y-2 text-red-700">
                    <li>• I authorize emergency medical personnel to provide necessary treatment</li>
                    <li>• I understand that I am responsible for all medical expenses incurred</li>
                    <li>• I release the Released Parties from any liability related to emergency medical treatment</li>
                    <li>• I acknowledge that the Released Parties are not responsible for any medical decisions made on my behalf</li>
                  </ul>
                </div>
              </section>

              {/* Golf Course Specific Risks */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Golf-Specific Risks & Acknowledgments</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">Golf Cart Safety</h3>
                    <ul className="space-y-1 text-blue-700 text-sm">
                      <li>• I will operate golf carts safely and responsibly</li>
                      <li>• I understand cart path rules and will comply</li>
                      <li>• I will keep hands and feet inside the cart at all times</li>
                      <li>• I will not exceed safe speeds or drive recklessly</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-green-800 mb-3">Playing Safety</h3>
                    <ul className="space-y-1 text-green-700 text-sm">
                      <li>• I will yell "FORE" when my ball may hit someone</li>
                      <li>• I will wait for clear areas before hitting</li>
                      <li>• I will stay alert for errant balls from other players</li>
                      <li>• I will follow all course safety rules and signage</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Alcohol Policy */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Alcohol Consumption Policy</h2>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                  <p className="text-orange-800 leading-relaxed mb-4">
                    Alcoholic beverages may be available during the Tournament. I acknowledge and agree that:
                  </p>
                  <ul className="space-y-2 text-orange-700">
                    <li>• Consumption of alcohol is voluntary and at my own risk</li>
                    <li>• I will consume alcohol responsibly and within legal limits</li>
                    <li>• I will not operate golf carts or participate if impaired</li>
                    <li>• The Released Parties are not responsible for my alcohol consumption or its effects</li>
                    <li>• I am responsible for arranging safe transportation after the event</li>
                  </ul>
                </div>
              </section>

              {/* Property & Personal Items */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Property & Personal Items</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  I understand and acknowledge that:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• The Released Parties are not responsible for lost, stolen, or damaged personal property</li>
                  <li>• I am responsible for securing my personal belongings at all times</li>
                  <li>• Golf equipment and personal items are brought at my own risk</li>
                  <li>• The golf course and event organizers provide no security for personal items</li>
                </ul>
              </section>

              {/* Photography & Media Release */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Photography & Media Release</h2>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                  <p className="text-purple-800 leading-relaxed">
                    I grant permission to the Released Parties to use photographs, videos, or other recordings of my participation 
                    in the Tournament for promotional, marketing, or other business purposes without compensation to me. 
                    I waive any right to inspect or approve the finished product or any written material that may be used 
                    in connection with such materials.
                  </p>
                </div>
              </section>

              {/* Legal Provisions */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Legal Provisions</h2>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Governing Law</h3>
                    <p>This agreement shall be governed by the laws of the State of Illinois.</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Severability</h3>
                    <p>If any provision of this agreement is found to be unenforceable, the remaining provisions shall remain in full force and effect.</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Entire Agreement</h3>
                    <p>This document represents the entire agreement between myself and the Released Parties regarding the subject matter herein.</p>
                  </div>
                </div>
              </section>

              {/* Acknowledgment */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Participant Acknowledgment</h2>
                <div className="bg-gray-900 text-white rounded-lg p-6">
                  <p className="leading-relaxed mb-4">
                    <strong>I HAVE READ THIS RELEASE OF LIABILITY AND ASSUMPTION OF RISK AGREEMENT, FULLY UNDERSTAND ITS TERMS, 
                    AND UNDERSTAND THAT I HAVE GIVEN UP SUBSTANTIAL RIGHTS BY SIGNING IT.</strong>
                  </p>
                  <p className="leading-relaxed mb-4">
                    I acknowledge that I am signing this agreement freely and voluntarily, and intend my signature to be a complete 
                    and unconditional release of all liability to the greatest extent allowed by law.
                  </p>
                  <p className="leading-relaxed text-red-300">
                    By checking the liability waiver checkbox during registration, I electronically sign and agree to be bound by this agreement.
                  </p>
                </div>
              </section>

              {/* Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions About This Waiver</h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <p className="text-gray-700 mb-4">
                    If you have questions about this liability waiver, please contact us before the tournament:
                  </p>
                  <div className="space-y-2 text-gray-700">
                    <p><strong>Phone:</strong> <a href="tel:+18156934361" className="text-red-600 hover:underline">(815) 693-4361</a></p>
                    <p><strong>Email:</strong> <a href="mailto:charityevent@crashchampions.com" className="text-red-600 hover:underline">charityevent@crashchampions.com</a></p>
                    <p><strong>Address:</strong> 601 Oakmont Lane, Westmont, IL 60559</p>
                  </div>
                </div>
              </section>

              <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-6">
                <p><strong>Effective Date:</strong> This waiver is effective for the 2026 Crash Champions Classic Golf Tournament</p>
                <p className="mt-2">Last Updated: April 30, 2026</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default LiabilityWaiversPage;